import { useState, useCallback } from 'react';
import { Search, Cloud, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../../../../config/api';

const SalesforceJobSection = ({ formData, setFormData, installations, updateInstallation }) => {
  const [jobQuery, setJobQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const formatSfTime = (timeStr) => {
    if (!timeStr) return '';
    const match = timeStr.match(/(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : '';
  };

  const isReasonableDate = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    const maxDate = new Date(now.getFullYear() + 2, now.getMonth(), now.getDate());
    return d <= maxDate;
  };

  const searchJob = useCallback(async () => {
    if (!jobQuery.trim()) return;
    setSearching(true);
    setError(null);
    setSearchResults(null);
    setLoaded(false);
    try {
      const res = await axios.get(`${API}/salesforce/job/${encodeURIComponent(jobQuery.trim())}`);
      applyJobData(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        try {
          const searchRes = await axios.get(`${API}/salesforce/jobs/search?q=${encodeURIComponent(jobQuery.trim())}`);
          if (searchRes.data.length > 0) {
            setSearchResults(searchRes.data);
          } else {
            setError('Nessun Job trovato in Salesforce');
          }
        } catch {
          setError('Nessun Job trovato in Salesforce');
        }
      } else {
        setError(err.response?.data?.detail || 'Errore connessione Salesforce');
      }
    } finally {
      setSearching(false);
    }
  }, [jobQuery]);

  const applyJobData = (job) => {
    const deviceTypeMap = {
      'SVR': 'SVR',
      'TELEMATICS': 'OBD',
      'GENERIC': 'SVR',
    };

    setFormData(prev => ({
      ...prev,
      sf_job_name: job.sf_job_name || '',
      sf_job_data: job,
      client_name: job.client_name || prev.client_name || '',
      client_company: job.site_customer_name || prev.client_company || '',
      site_name: job.site_name || prev.site_name || '',
      site_code: job.site_code || prev.site_code || '',
      address: job.address || job.city || prev.address || '',
      site_address: job.address || job.city || prev.site_address || '',
      city: job.city || prev.city || '',
      site_city: job.city || prev.site_city || '',
      province: job.province || prev.province || '',
      site_province: job.province || prev.site_province || '',
      postal_code: job.postal_code || prev.postal_code || '',
      country: job.country || prev.country || '',
      vehicle_make: job.vehicle_make || prev.vehicle_make || '',
      vehicle_model: job.vehicle_model || prev.vehicle_model || '',
      vehicle_vin: job.vehicle_vin || prev.vehicle_vin || '',
      vehicle_plate: job.vehicle_vrm || prev.vehicle_plate || '',
      device_type: deviceTypeMap[job.product_group] || prev.device_type || '',
      contact_name: job.contact_name || prev.contact_name || '',
      contact_phone: job.contact_phone || prev.contact_phone || '',
      intervention_type: job.sf_job_type === 'Install' ? 'installation' : (job.sf_job_type === 'Deinstall' ? 'deinstallation' : prev.intervention_type || ''),
      scheduled_date: isReasonableDate(job.booked_date) ? job.booked_date : (isReasonableDate(job.preferred_date) ? job.preferred_date : prev.scheduled_date || new Date().toISOString().split('T')[0]),
      scheduled_time: formatSfTime(job.booked_time || job.preferred_time) || prev.scheduled_time || '',
      notes: [
        prev.notes || '',
        job.sales_source ? `Richiedente: ${job.sales_source}` : '',
        job.customer_po ? `PO: ${job.customer_po}` : '',
        job.job_notes || '',
      ].filter(Boolean).join('\n'),
    }));

    // Pre-fill first installation vehicle fields
    if (installations && installations.length > 0 && updateInstallation) {
      const firstId = installations[0].id;
      if (job.sf_job_name) updateInstallation(firstId, 'job_number', job.sf_job_name);
      if (job.vehicle_make) updateInstallation(firstId, 'vehicle_brand', job.vehicle_make);
      if (job.vehicle_model) updateInstallation(firstId, 'vehicle_model', job.vehicle_model);
      if (job.vehicle_vrm) updateInstallation(firstId, 'license_plate', job.vehicle_vrm);
      if (job.vehicle_vin) updateInstallation(firstId, 'chassis_number', job.vehicle_vin);
      const dtMap = { 'SVR': 'SVR', 'TELEMATICS': 'OBD', 'GENERIC': 'SVR' };
      if (job.product_group && dtMap[job.product_group]) {
        updateInstallation(firstId, 'device_type', dtMap[job.product_group]);
      }
    }

    setLoaded(true);
    setSearchResults(null);
    setError(null);
  };

  const selectResult = (job) => {
    setJobQuery(job.sf_job_name);
    applyJobData(job);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchJob();
    }
  };

  return (
    <div className="border border-blue-200 rounded-lg p-4 bg-gradient-to-r from-blue-50 to-indigo-50" data-testid="salesforce-job-section">
      <div className="flex items-center gap-2 mb-3">
        <Cloud size={18} className="text-blue-600" />
        <h3 className="font-semibold text-blue-900 text-sm">Salesforce Job</h3>
        {loaded && <CheckCircle size={16} className="text-green-500 ml-auto" />}
      </div>

      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            type="text"
            value={jobQuery}
            onChange={(e) => setJobQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Inserisci numero Job (es. JB-5700091)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
            data-testid="sf-job-input"
          />
          {searching && <Loader2 size={16} className="absolute right-2 top-2.5 text-blue-500 animate-spin" />}
        </div>
        <button
          type="button"
          onClick={searchJob}
          disabled={searching || !jobQuery.trim()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition"
          data-testid="sf-job-search-btn"
        >
          <Search size={14} />
          Cerca
        </button>
      </div>

      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-1" data-testid="sf-job-error">
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {searchResults && searchResults.length > 0 && (
        <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto" data-testid="sf-job-results">
          {searchResults.map((job, i) => (
            <button
              key={i}
              type="button"
              onClick={() => selectResult(job)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition"
            >
              <span className="font-medium text-blue-700">{job.sf_job_name}</span>
              <span className="text-gray-600 ml-2">{job.client_name}</span>
              {job.vehicle_vrm && <span className="text-gray-400 ml-2">{job.vehicle_vrm}</span>}
              {job.city && <span className="text-gray-400 ml-2">- {job.city}</span>}
            </button>
          ))}
        </div>
      )}

      {loaded && formData.sf_job_data && (
        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800" data-testid="sf-job-loaded">
          <p className="font-semibold text-green-900 mb-1.5">Dati importati da Salesforce:</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <span><strong>Job:</strong> {formData.sf_job_name}</span>
            {formData.sf_job_data.sf_job_type && <span><strong>Tipo Job:</strong> {formData.sf_job_data.sf_job_type}</span>}
            {formData.sf_job_data.sf_job_status && <span><strong>Stato SF:</strong> {formData.sf_job_data.sf_job_status}</span>}
            {formData.sf_job_data.client_name && <span><strong>Cliente:</strong> {formData.sf_job_data.client_name}</span>}
            {formData.sf_job_data.sales_source && <span><strong>Richiedente:</strong> {formData.sf_job_data.sales_source}</span>}
            {formData.sf_job_data.site_name && <span><strong>SITE:</strong> {formData.sf_job_data.site_name}</span>}
            {formData.sf_job_data.site_customer_name && <span><strong>Rag. Sociale SITE:</strong> {formData.sf_job_data.site_customer_name}</span>}
            {formData.sf_job_data.address && <span><strong>Indirizzo:</strong> {formData.sf_job_data.address}</span>}
            {formData.sf_job_data.city && <span><strong>Citta:</strong> {formData.sf_job_data.city} {formData.sf_job_data.province}</span>}
            {formData.sf_job_data.postal_code && <span><strong>CAP:</strong> {formData.sf_job_data.postal_code}</span>}
            {formData.sf_job_data.vehicle_make && <span><strong>Veicolo:</strong> {formData.sf_job_data.vehicle_make} {formData.sf_job_data.vehicle_model}</span>}
            {formData.sf_job_data.vehicle_vrm && <span><strong>Targa:</strong> {formData.sf_job_data.vehicle_vrm}</span>}
            {formData.sf_job_data.vehicle_vin && <span><strong>VIN:</strong> {formData.sf_job_data.vehicle_vin}</span>}
            {formData.sf_job_data.product_group && <span><strong>Prodotto:</strong> {formData.sf_job_data.product_group}</span>}
            {formData.sf_job_data.contact_name && <span><strong>Contatto:</strong> {formData.sf_job_data.contact_name}</span>}
            {formData.sf_job_data.contact_phone && <span><strong>Telefono:</strong> {formData.sf_job_data.contact_phone}</span>}
            {formData.sf_job_data.customer_po && <span><strong>PO:</strong> {formData.sf_job_data.customer_po}</span>}
            {formData.sf_job_data.preferred_date && <span><strong>Data Preferita:</strong> {formData.sf_job_data.preferred_date}</span>}
            {formData.sf_job_data.preferred_time && <span><strong>Ora Preferita:</strong> {formData.sf_job_data.preferred_time.substring(0, 5)}</span>}
            {formData.sf_job_data.booked_date && <span><strong>Data Prenotata:</strong> {formData.sf_job_data.booked_date}</span>}
            {formData.sf_job_data.booked_time && <span><strong>Ora Prenotata:</strong> {formData.sf_job_data.booked_time.substring(0, 5)}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesforceJobSection;
